import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import { useEffect } from "react";
import { TaskType } from "api";
import { Stack } from "@mui/joy";
import { useRecoilState, useRecoilValue } from "recoil";
import { menuSelector } from "recoil/menuAtom";
import TaskItem from "./TaskItem";
import { UpArrowSvg } from "static/image/common/SvgAssets";
import { MeditationItemType, ThoughtRecordItemType } from "api/homeApi";
import ToolItem from "./ToolItem";
import { automaticThoughtIdType } from "api/conceptApi";
import { userAtom } from "../../recoil/userAtom";
import { theme } from "../../styles/theme";

export type menuKeyType =
  | "taskList_open"
  | "taskList_close"
  | "thoughtRecordList_open"
  | "thoughtRecordList_close"
  | "meditationList_open"
  | "meditationList_close";

export default function NestedList({
  menuKey,
  title,
  taskList,
  thoughtRecordList,
  meditationList,
  initiallyOpen,
}: {
  menuKey: menuKeyType;
  title: string;
  taskList?: TaskType[];
  thoughtRecordList?: ThoughtRecordItemType[];
  meditationList?: MeditationItemType[];
  initiallyOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useRecoilState(menuSelector(menuKey));
  const userState = useRecoilValue(userAtom);

  useEffect(() => {
    if (initiallyOpen) {
      setIsOpen(true);
    }
  }, [initiallyOpen]);

  function getAutomaticThoughtId(taskId: string) {
    const automaticThoughtId = taskId
      .split("_")[1]
      ?.split("#")[0] as automaticThoughtIdType;
    return automaticThoughtId || 0;
  }

  // Parse ordering keys for "90-1-A" tasks: primary by number after "@", secondary by number after "_"
  function parse90_1_A_Order(
    taskId: string
  ): { atNum: number; underNum: number } | null {
    if (!taskId.startsWith("90-1-A")) return null;
    const atMatch = taskId.match(/@(\d+)/);
    const underMatch = taskId.match(/_(\d+)/);
    const atNum = atMatch ? parseInt(atMatch[1], 10) : 0;
    const underNum = underMatch ? parseInt(underMatch[1], 10) : 0;
    return { atNum, underNum };
  }

  taskList
    ?.sort((a, b) => {
      const setNumberA = a.setNumber ?? 0;
      const setNumberB = b.setNumber ?? 0;

      return setNumberA - setNumberB;
    })
    ?.sort((a, b) => {
      const automaticThoughtIdA = getAutomaticThoughtId(a.taskId);
      const automaticThoughtIdB = getAutomaticThoughtId(b.taskId);
      if (!a.taskId.startsWith("7-") && !b.taskId.startsWith("7-")) {
        if (automaticThoughtIdA < automaticThoughtIdB) return -1;
        if (automaticThoughtIdA > automaticThoughtIdB) return 1;
        return 0;
      }

      return 0;
    })
    ?.sort((a, b) => {
      const automaticThoughtIdA = getAutomaticThoughtId(a.taskId);
      const automaticThoughtIdB = getAutomaticThoughtId(b.taskId);
      if (a.taskId.startsWith("7-") && b.taskId.startsWith("7-")) {
        if (automaticThoughtIdA < automaticThoughtIdB) return -1;
        if (automaticThoughtIdA > automaticThoughtIdB) return 1;
        return 0;
      }
      return 0;
    })
    ?.sort((a, b) => {
      const a90 = parse90_1_A_Order(a.taskId);
      const b90 = parse90_1_A_Order(b.taskId);
      const aIs90 = !!a90;
      const bIs90 = !!b90;

      // Move 90-1-A tasks to the end
      if (aIs90 && !bIs90) return 1;
      if (!aIs90 && bIs90) return -1;

      // If both are 90-1-A, sort by openAt ascending
      if (aIs90 && bIs90) {
        const aTime = a.openAt
          ? new Date(a.openAt).getTime()
          : Number.MAX_SAFE_INTEGER;
        const bTime = b.openAt
          ? new Date(b.openAt).getTime()
          : Number.MAX_SAFE_INTEGER;
        return aTime - bTime;
      }

      // Neither is 90-1-A: keep prior ordering
      return 0;
    });

  if (taskList) {
    const getThoughtTrapNum = (taskId: string) => {
      return taskId.split("-")[2];
    };
    for (let i = taskList.length - 1; i >= 0; i--) {
      const currentTask = taskList[i];
      const match44 = currentTask.taskId.match(/^4-4-(\d+)-A/);

      if (match44) {
        for (let j = i + 1; j < taskList.length; j++) {
          const nextTask = taskList[j];
          const match45 = nextTask.taskId.match(/^4-5-(\d+)-A/);
          if (
            match45 &&
            getThoughtTrapNum(currentTask.taskId) ===
              getThoughtTrapNum(nextTask.taskId)
          ) {
            // "4-4-{n}-A"를 "4-5-{n}-A" 바로 앞에 놓기
            const task44 = taskList.splice(i, 1)[0]; // "4-4-{n}-A" 제거
            taskList.splice(j - 1, 0, task44); // "4-5-{n}-A" 앞에 삽입
            break;
          }
        }
      }
    }
  }

  // 인접 두 항목이 같은 묶음에 속하는지 (= 사이에 묶음 선이 그어지는지) 판정.
  // hasConnectionLine 의 기존 조건과 동일.
  function connects(a: TaskType, b: TaskType) {
    const atiA = getAutomaticThoughtId(a.taskId);
    const atiB = getAutomaticThoughtId(b.taskId);
    return (
      (!!atiA && atiA === atiB) ||
      a.setNumber === b.setNumber ||
      // 다관생에서 생각함정 잇기 위함
      (a.taskId.startsWith("4-2") && b.taskId.startsWith("4-4-"))
    );
  }

  return (
    <Box>
      {(thoughtRecordList || meditationList) && (
        <Stack
          direction="row"
          spacing="8px"
          alignItems="center"
          onClick={() => {
            setIsOpen((isOpen) => !isOpen);
          }}
          sx={{ py: "8px", cursor: "pointer" }}
        >
          <Box
            sx={{
              ...(!isOpen && {
                transform: "rotate(-90deg)",
                transformOrigin: "center",
              }),
              transition: "transform 0.2s",
            }}
          >
            <UpArrowSvg color={theme.vars.palette.Content.Weak} />
          </Box>
          <Typography
            variant={"Caption"}
            sx={{
              fontWeight: 500,
              color: theme.vars.palette.Content.Weak,
              px: 0,
            }}
          >
            {title}
          </Typography>
        </Stack>
      )}

      {isOpen && (
        <Stack sx={{ pl: 2 }} spacing={"4px"}>
          {taskList ? (
            taskList.map(
              (
                {
                  taskKey,
                  isOpen,
                  isDone,
                  isLocked,
                  isOptional,
                  hasMissingValue,
                  linkedThoughtRecordKey,
                  linkedMeditationRecordKey,
                  setNumber,
                },
                index
              ) => {
                const isConnectedDown =
                  index < taskList.length - 1 &&
                  connects(taskList[index], taskList[index + 1]);
                const isConnectedUp =
                  index > 0 && connects(taskList[index - 1], taskList[index]);
                return (
                  <TaskItem
                    key={taskKey}
                    taskKey={taskKey}
                    isOpen={isOpen}
                    isDone={isDone}
                    isLocked={isLocked}
                    isOptional={isOptional}
                    hasMissingValue={hasMissingValue}
                    linkedThoughtRecordKey={linkedThoughtRecordKey}
                    linkedMeditationRecordKey={linkedMeditationRecordKey}
                    setNumber={setNumber}
                    isConnectedUp={isConnectedUp}
                    isConnectedDown={isConnectedDown}
                  />
                );
              }
            )
          ) : thoughtRecordList ? (
            thoughtRecordList.map(
              ({ thoughtRecordKey, title, date, isOpen, isDone }, index) => (
                <ToolItem
                  key={thoughtRecordKey}
                  thoughtRecordKey={thoughtRecordKey}
                  title={title}
                  date={date}
                  isOpen={isOpen}
                  isDone={isDone}
                />
              )
            )
          ) : meditationList ? (
            meditationList.map(
              ({ meditationRecordKey, title, date, isOpen, isDone }, index) => (
                <ToolItem
                  key={meditationRecordKey}
                  meditationRecordKey={meditationRecordKey}
                  title={title}
                  date={date}
                  isOpen={isOpen}
                  isDone={isDone}
                />
              )
            )
          ) : (
            <></>
          )}
        </Stack>
      )}
    </Box>
  );
}
